import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactHighcharts from 'react-highcharts';
import numeral from 'numeral';
import { Dropdown, MenuItem } from 'react-bootstrap';
import {
  delay, orderBy, first, reduce, cloneDeep,
  each, capitalize, find, filter, uniq, keyBy,
} from 'lodash';
import { getIsFontSizeBig } from '../../../App/AppReducer';

import { getColorFromKey } from '../../../../util/helpers';
import { getAccountIndustryFilter } from '../../../../components/search-form/search-form-reducer';
import { format as formatMoney } from '../../../../components/money-display';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';

// Import Selectors
import {
  getWinLossOpportunities,
} from '../../../Dashboard/DashboardReducer';

const USER_PREFERENCE_REPORT_TYPE = 'leaderboard-industry-graph-report-type';
const USER_PREFERENCE_FIELD_MAPPING_TYPE = 'leaderboard-industry-graph-mapping-type';

class IndustryChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportType: 'amount', // 'amount', 'count'
      mappingType: null, // check this depend on props
      categories: [],
    };
    this.setReportType = this.setReportType.bind(this);
    this.setMappingType = this.setMappingType.bind(this);
    this.calculate = this.calculate.bind(this);
  }

  componentWillMount() {
    const { hasIndustryMappings } = this.props;
    const reportType = this.context.getUserPreference(USER_PREFERENCE_REPORT_TYPE, 'amount');
    let mappingType = this.context.getUserPreference(USER_PREFERENCE_FIELD_MAPPING_TYPE, 'mapping');
    if (!hasIndustryMappings) {
      mappingType = 'field';
    }
    this.setState({
      reportType, mappingType,
    }, this.calculate);
  }

  setReportType(reportType) {
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
    const categories = orderBy(this.state.categories, reportType, ['desc']);
    this.setState({ categories, reportType }, this.calculate);
  }

  setMappingType(mappingType) {
    this.setState({ mappingType }, this.calculate);
    this.context.updateUserPreference(USER_PREFERENCE_FIELD_MAPPING_TYPE, mappingType);
  }

  calculate() {
    // switch the categories
    const { accountIndustryMappings, accountIndustryValues, opportunities } = this.props;
    const { mappingType, reportType } = this.state;
    let categories = [];
    const oneStat = {
      count: 0,
      amount: 0,
      counts: { Won: 0, Lost: 0 },
      amounts: { Won: 0, Lost: 0 },
      opportunityIds: { Won: [], Lost: [] },
    };
    if (mappingType === 'mapping') {
      categories = reduce(accountIndustryMappings, (items, mapping) => items.concat({
        id: mapping._id,
        label: mapping.label,
        values: mapping.values,
        ...(cloneDeep(oneStat)),
      }), []);
    } else {
      categories = reduce(accountIndustryValues, (items, val) => items.concat({
        id: val._id,
        label: val.label,
        values: [val.industry_id],
        ...(cloneDeep(oneStat)),
      }), []);
    }
    const indexedCategories = keyBy(categories, 'id');

    const reverseMappings = reduce(categories, (items, category) => {
      each(category.values, (value) => {
        if (!items[value]) {
          items[value] = [];
        }
        items[value] = uniq(items[value].concat(category.id));
      });
      return items;
    }, {});

    each(opportunities, (opportunity) => {
      const industries = opportunity.account ? opportunity.account.industries : [];
      const stage = opportunity.has_requests ? 'Won' : 'Lost';
      const amount = opportunity.amount || 0;
      const oCategories = reduce(industries, (items, industry) => {
        if (reverseMappings[industry]) {
          return uniq(items.concat(reverseMappings[industry]));
        }
        return items;
      }, []);
      each(oCategories, (category) => {
        if (indexedCategories[category]) {
          indexedCategories[category].count += 1;
          indexedCategories[category].amount += amount;

          if (indexedCategories[category].counts[stage] !== undefined) {
            indexedCategories[category].counts[stage] += 1;
          }
          if (indexedCategories[category].amounts[stage] !== undefined) {
            indexedCategories[category].amounts[stage] += amount;
          }
          if (indexedCategories[category].opportunityIds[stage] !== undefined) {
            indexedCategories[category].opportunityIds[stage].push(opportunity.opportunity_id);
          }
        }
      });
    });
    const nonEmptyCategories = filter(categories, category => category.count > 0);
    const sortedCategories = orderBy(nonEmptyCategories, reportType, ['desc']);

    this.setState({ categories: sortedCategories });
  }

  afterRender(chart) {
    const ext = chart.yAxis[0].getExtremes();
    const dMax = Math.abs(ext.dataMax);
    const dMin = Math.abs(ext.dataMin);
    const dExt = dMax >= dMin ? dMax : dMin;
    const min = 0 - dExt;
    chart.yAxis[0].setExtremes(min, dExt);
  }
  renderGraph() {
    const {
      opportunities, organization,
      isFontSizeBig,
    } = this.props;
    if (opportunities.length === 0) {
      return null;
    }
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const {
      categories, reportType,
    } = this.state;

    let yAxisTitle = capitalize(reportType);
    if (reportType === 'amount' && suffix && suffix.length) {
      yAxisTitle += ` (${suffix})`;
    }
    yAxisTitle += ' With and Without Feedback';
    
    const series = [];
    
    series.push({
      name: 'Requested Feedback', stage: 'Won', color: getColorFromKey('won'), data: [],
    });

    series.push({
      name: 'No Requested', stage: 'Lost', color: getColorFromKey('lost'), data: [],
    });

    each(categories, (category) => {
      each(series, (s) => {
        const { stage } = s;
        const modifier = stage === 'Won' ? 1 : -1;
        let y = 0;
        if (category[reportType === 'amount' ? 'amounts' : 'counts'][stage]) {
          y = modifier * (category[reportType === 'amount' ? 'amounts' : 'counts'][stage] || 0);
        }
        const absY = Math.abs(y);
        let formatedY = 0;
        if (absY > 0) {
          if (reportType === 'amount') {
            formatedY = formatMoney(absY, true, 1, symbol, suffix);
          } else {
            formatedY = numeral(absY).format('0');
          }
        }
        s.data.push({
          y,
          formatedY,
          opportunityIds: category.opportunityIds[stage],
          events: {
            click: (event) => {
              this.props.onOpportunitySelect(event.point.options.opportunityIds);
            },
          },
        });
      });
    });

    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'bar',
        height: Math.max(isFontSizeBig ? 600 : 400, 100 + (categories.length * (isFontSizeBig ? 40 : 30))),
        animation: {
          duration: 1000,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        crosshair: true,
        alternateGridColor: 'rgba(66,66,66,0.05)',
        labels: {
          formatter: function f() {
            return `${this.value.label}`;
          },
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
      },
      tooltip: {
        headerFormat: `<div><span style="font-size:16px;font-weight: bold;">{point.key.label}</span></div><table style="${isFontSizeBig ? 'font-size: 16px;' : ''};margin-top: 1em; margin-bottom: 0;" class="table table-condensed">`,
        pointFormat: `<tr>
          <td style="color:{series.color};">{series.name}:&nbsp;</td>
          <td class="text-right"><b>{point.formatedY}</b></td>
        </tr>`,
        footerFormat: '</table>',
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
        positioner: function p(labelWidth, labelHeight, point) {
          let tooltipX = 0;
          let tooltipY = 0;
          const { chart } = this;
          const marginX = 20;
          const marginY = 20;
          if (point.plotX + labelWidth > chart.plotWidth) {
            tooltipX = (point.plotX + chart.plotLeft) - labelWidth - marginX;
          } else {
            tooltipX = point.plotX + chart.plotLeft + marginX;
          }

          if ((point.plotY + chart.plotTop) - labelHeight - marginY > 0) {
            tooltipY = (point.plotY + chart.plotTop) - labelHeight - marginY;
          } else {
            tooltipY = point.plotY + chart.plotTop + marginY;
          }

          return {
            x: tooltipX,
            y: tooltipY,
          };
        },
      },
      yAxis: [
        
        {
          title: {
            text: yAxisTitle,
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          opposite: true,
          tickAmount: 9,
          labels: {
            formatter: function f() {
              let v = 0;
              if (this.value !== 0) {
                if (reportType === 'amount') {
                  v = formatMoney(this.value, true, 1, symbol);
                } else {
                  v = numeral(this.value).format('0');
                }
              }
              return v;
            },
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          lineWidth: 0,
          plotLines: [{
            value: 0,
            dashStyle: 'dashed',
            color: 'rgba(0, 0, 0, 0.5)',
            width: 2,
            label: {
              text: null,
            },
          }],
        },
      ],
      plotOptions: {
        series: {
          stacking: 'normal',
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
        },
      },
      legend: {
        reversed: true,
        itemStyle: {
          fontSize: isFontSizeBig ? '16px' : null,
        },
      },
      series,
    };

    return (
      <div>
        <ReactHighcharts config={config} isPureConfig callback={this.afterRender} />
      </div>
    );
  }

  render() {
    const {
      opportunities, hasIndustryMappings,
    } = this.props;
    if (opportunities.length === 0) {
      return null;
    }
    const {
      reportType, mappingType,
    } = this.state;
 

    const reportTypes = [
      { value: 'amount', label: 'Amount' },
      { value: 'count', label: 'Count' },
    ];
    const activeReportType = find(reportTypes, { value: reportType }) || first(reportTypes);

    const mappingTypes = [
      { value: 'field', label: 'Fields', disabled: false },
      { value: 'mapping', label: 'Mappings', disabled: !hasIndustryMappings },
    ];
    let activeMappingType = find(mappingTypes, { value: mappingType }) || first(mappingTypes);
    if (!hasIndustryMappings) {
      activeMappingType = { value: 'field', label: 'Fields' };
    }
    return (
      <div>
        <div className="animated fadeIn">
          <div>
            <h5 className="sub-header-with-controls">
              View
              &nbsp;
              <Dropdown
                id="leaderboard-report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeReportType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {reportTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === activeReportType.value}
                      onSelect={() => { this.setReportType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>feedback request rates and use industry </span>
              &nbsp;
              <Dropdown
                id="leaderboard-report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeMappingType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {mappingTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === activeMappingType.value}
                      onSelect={() => { this.setMappingType(s.value); }}
                      disabled={s.disabled}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            </h5>
          </div>
          {this.renderGraph()}
        </div>
      </div>
    );
  }
}

IndustryChart.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  accountIndustryMappings: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  accountIndustryValues: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    industry_id: PropTypes.string.isRequired,
  })).isRequired,
  hasIndustryMappings: PropTypes.bool.isRequired,
  onOpportunitySelect: PropTypes.func,
  isFontSizeBig: PropTypes.bool.isRequired,
};

IndustryChart.defaultProps = {
  organization: null,
  onOpportunitySelect: () => {},
};

IndustryChart.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  getUserPreference: PropTypes.func.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  const accountIndustryFilter = getAccountIndustryFilter(state);
  const accountIndustryMappings = accountIndustryFilter ? accountIndustryFilter.mappings : [];
  const accountIndustryValues = accountIndustryFilter ? accountIndustryFilter.values : [];
  const hasIndustryMappings = (filter(accountIndustryMappings, m => m.values.length > 0)).length > 0;
  return {
    opportunities: getWinLossOpportunities(state),
    organization: getUserCurrentOrganization(state),
    accountIndustryMappings,
    hasIndustryMappings,
    accountIndustryValues,
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

export default connect(mapStateToProps)(IndustryChart);
